import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import { formatDateLocal } from "../../../utils/transactionUtils";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { TransactionCustomFieldValues } from "../../../types";

interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface UseActionButtonHandlersProps {
  setDialogType: (type: "expense" | "income") => void;
  setEditingTransaction: (transaction: TransactionWithDate | null) => void;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  setShowTime: (show: boolean) => void;
  setShowTransferTime: (show: boolean) => void;
  setShowTransferFee?: (show: boolean) => void;
  setSelectedCategoryDetails: (details: any[]) => void;
  setNewlyAddedDetailOptions: (options: Record<string, string[]>) => void;
  setDetailInputValues: (values: Record<string, string>) => void;
  setOpenDetailDropdowns: (dropdowns: Set<string>) => void;
  setRateManuallyEdited: (edited: boolean) => void;
  setTransferRateManuallyEdited: (edited: boolean) => void;
  setDialogOpen: (open: boolean) => void;
  setTransferDialogOpen: (open: boolean) => void;
}

/**
 * Hook to handle action button clicks (Expense, Income, Transfer).
 * On mobile: navigates to mobile transaction page
 * On desktop: opens dialog
 */
export const useActionButtonHandlers = ({
  setDialogType,
  setEditingTransaction,
  setFormData,
  setTransferFormData,
  setShowTime,
  setShowTransferTime,
  setSelectedCategoryDetails,
  setNewlyAddedDetailOptions,
  setDetailInputValues,
  setOpenDetailDropdowns,
  setRateManuallyEdited,
  setTransferRateManuallyEdited,
  setDialogOpen,
  setTransferDialogOpen,
}: UseActionButtonHandlersProps): {
  handleExpenseClick: () => void;
  handleIncomeClick: () => void;
  handleTransferClick: () => void;
} => {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleExpenseClick = useCallback((): void => {
    // On mobile, navigate to mobile transaction page
    if (isMobile && workspaceSlug) {
      navigate(`/${workspaceSlug}/create-transaction/expense`);
      return;
    }

    // On desktop, open dialog
    setDialogType("expense");
    setEditingTransaction(null);
    setFormData({
      title: "",
      category: "",
      amount: "",
      date: formatDateLocal(new Date()),
      time: "",
      accountId: "",
      accountGroup: "",
      currency: "",
      rate: "",
      defaultCurrencyRate: "",
      details: undefined,
    });
    setShowTime(false);
    setSelectedCategoryDetails([]);
    setNewlyAddedDetailOptions({});
    setDetailInputValues({});
    setOpenDetailDropdowns(new Set());
    setRateManuallyEdited(false);
    setDialogOpen(true);
  }, [
    isMobile,
    workspaceSlug,
    navigate,
    setDialogType,
    setEditingTransaction,
    setFormData,
    setShowTime,
    setSelectedCategoryDetails,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
    setOpenDetailDropdowns,
    setRateManuallyEdited,
    setDialogOpen,
  ]);

  const handleIncomeClick = useCallback((): void => {
    // On mobile, navigate to mobile transaction page
    if (isMobile && workspaceSlug) {
      navigate(`/${workspaceSlug}/create-transaction/income`);
      return;
    }

    // On desktop, open dialog
    setDialogType("income");
    setEditingTransaction(null);
    setFormData({
      title: "",
      category: "",
      amount: "",
      date: formatDateLocal(new Date()),
      time: "",
      accountId: "",
      accountGroup: "",
      currency: "",
      rate: "",
      defaultCurrencyRate: "",
      details: undefined,
    });
    setShowTime(false);
    setSelectedCategoryDetails([]);
    setNewlyAddedDetailOptions({});
    setDetailInputValues({});
    setOpenDetailDropdowns(new Set());
    setRateManuallyEdited(false);
    setDialogOpen(true);
  }, [
    isMobile,
    workspaceSlug,
    navigate,
    setDialogType,
    setEditingTransaction,
    setFormData,
    setShowTime,
    setSelectedCategoryDetails,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
    setOpenDetailDropdowns,
    setRateManuallyEdited,
    setDialogOpen,
  ]);

  const handleTransferClick = useCallback((): void => {
    // On mobile, navigate to mobile transaction page
    if (isMobile && workspaceSlug) {
      navigate(`/${workspaceSlug}/create-transaction/transfer`);
      return;
    }

    // On desktop, open dialog
    setEditingTransaction(null);
    setTransferFormData({
      amount: "",
      date: formatDateLocal(new Date()),
      time: "",
      fromAccountId: "",
      toAccountId: "",
      transferRate: "",
    });
    setShowTransferTime(false);
    setTransferRateManuallyEdited(false);
    setTransferDialogOpen(true);
  }, [
    isMobile,
    workspaceSlug,
    navigate,
    setEditingTransaction,
    setTransferFormData,
    setShowTransferTime,
    setTransferRateManuallyEdited,
    setTransferDialogOpen,
  ]);

  return {
    handleExpenseClick,
    handleIncomeClick,
    handleTransferClick,
  };
};
